import ChargeHelper, { ChargeConstants } from 'features/Charge/chargeHelper';
import DropdownFilterTable from 'general/components/DropdownFilterTable';
import DropdownTableConfigColumn from 'general/components/DropdownTableConfigColumn';
import KTFormSearch from 'general/components/OtherKeenComponents/Forms/KTFormSearch';
import KTBSDropdown from 'general/components/OtherKeenComponents/KTBSDropdown';
import KTDropdownFilter from 'general/components/OtherKeenComponents/KTDropdownFilter';
import KTLabel, { KTLabelSize } from 'general/components/OtherKeenComponents/KTLabel';
import KTPagination, { KTPaginationColor } from 'general/components/OtherKeenComponents/KTPagination';
import KTTable from 'general/components/OtherKeenComponents/KTTable';
import tableStyle from 'general/components/OtherKeenComponents/KTTable/tableStyle';
import CustomDropdown2 from 'general/components/Others/CustomDropdown2';
import AppColors from 'general/constants/AppColors';
import AppConfigs from 'general/constants/AppConfigs';
import UIHelper from 'general/helpers/UIHelper';
import Global from 'general/utils/Global';
import Utils from 'general/utils/Utils';
import useConfigTableColumn from 'hooks/useConfigTableColumn';
import useRouter from 'hooks/useRouter';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import ChargePointListFilterView from '../ChargePointListFilterView';
import AppConstants from 'general/constants/AppConstants';
import { dateFilter } from 'general/components/Others/CustomDateRangePicker';
import MaintainerHelper from 'features/Maintainer/maintainerHelper';
import KTTooltip from 'general/components/OtherKeenComponents/KTTooltip';
import AppRoute from 'general/constants/AppRoute';

ChargePointListView.propTypes = {
  stationId: PropTypes.string,
  hasAppTab: PropTypes.bool,
  chargePointListFilter: PropTypes.object,
  onFilterChanged: PropTypes.func,
};

const defaultColumnIds = [
  'CODE',
  'NAME',
  'STATION',
  'VEHICLE_TYPE',
  'OCPP',
  'PHONE',
  'MODEL',
  'TYPE',
  'STATUS',
  'POWER',
  'CONNECTOR_NUM',
  'PRICING_MODE',
  'PRICE',
  'FREE_DURATION',
  'OVER_PRICE',
  'RESERVATION_PRICE',
  'MAINTENANCE_SCHEDULE'
];

function ChargePointListView({ stationId, hasAppTab = false, chargePointListFilter, enabledEdit = true }) {
  // MARK: ---- PROPERTIES ----
  const router = useRouter();
  const { user, permissionList } = useSelector((state) => state.auth);
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'OPERATOR';
  const prefKey = useMemo(() => {
    const userId = user.userId;
    const pathName = router.pathname;
    const key = `${userId}_${pathName}`;
    return key;
  }, [user, router]);
  const [filter, setFilter] = useState();
  const [filterDropdown, setFilterDropdown] = useState(chargePointListFilter);
  const [filteredItems, setFilteredItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [availableTotal, setAvailableTotal] = useState(0);
  const isAdminAgency = useMemo(() => {
    if (user) {
      return user?.role === 'AGENCY' && user?.agencyId === 1;
    }
    return false;
  }, [user])

  useEffect(() => {
    if (chargePointListFilter) {
      setFilter(chargePointListFilter);
    } else {
      setFilter({
        page: 0,
        limit: AppConfigs.defaultPageLimit,
        q: '',
        status: '',
        type: '',
        stationId: '',
        vehicleType: '',
        chargingSpeed: '',
      });
    }
  }, [chargePointListFilter]);

  useEffect(() => {
    if (!filter) return;
    const params = _.pickBy(
      {
        ...filter,
        stationId: stationId ?? filter?.stationId,
      },
      (value) => value !== null && value !== undefined && value !== ''
    );

    ChargeHelper.API.requestGetChargePointList(params).then((data) => {
      setFilteredItems(data?.chargePoints);
      setTotal(data?.total);
    });

    ChargeHelper.API.requestGetChargePointList({
      ...params,
      status: ChargeConstants.ChargePointStatus.AVAILABLE,
    }).then((data) => {
      setAvailableTotal(data?.total);
    });
  }, [filter, stationId]);

  const fullColumns = useMemo(() => {
    return [
      {
        id: 'CODE',
        name: (
          <span
            className="letter-spacing-unset"
            style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
          >
            Mã trụ sạc
          </span>
        ),
        selector: (row) => row?.pointCode,
        width: '120px',
        style: tableStyle.columns.link,
      },
      {
        id: 'NAME',
        name: (
          <span
            className="letter-spacing-unset"
            style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
          >
            Tên trụ sạc
          </span>
        ),
        selector: (row) => row?.name,
        width: '120px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'STATION',
        omit: !!stationId,
        name: (
          <span
            className="letter-spacing-unset"
            style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
          >
            Trạm sạc
          </span>
        ),
        selector: (row) => (
          <KTTooltip offset={[0, 0]} text={row?.chargeStation?.name}>
            <span
              className="cursor-pointer hover-opacity-60"
              onClick={() => {
                Utils.openInNewTab(`${AppRoute.stationList}/${row?.chargeStation?.stationId ?? ''}`)
              }}
            >
              {row?.chargeStation?.name}
            </span>
          </KTTooltip>
        ),
        minWidth: '200px',
        style: tableStyle.columns.link,
      },
      {
        id: 'VEHICLE_TYPE',
        name: (
          <span
            className="letter-spacing-unset"
            style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
          >
            Loại phương tiện
          </span>
        ),
        selector: (row) =>
          row?.chargeModel?.vehicleType
            ? _.find(
              ChargeHelper.Data.vehicleTypeOptions,
              (item) => item.value === row?.chargeModel?.vehicleType
            )?.name
            : '',
        width: '130px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'OCPP',
        name: (
          <span
            className="letter-spacing-unset"
            style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
          >
            Mã OCPP
          </span>
        ),
        selector: (row) => row?.ocppId,
        minWidth: '180px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'PHONE',
        name: (
          <span
            className="letter-spacing-unset"
            style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
          >
            Số điện thoại
          </span>
        ),
        selector: (row) => row?.phoneNumber,
        minWidth: '180px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'MODEL',
        name: (
          <span
            className="letter-spacing-unset"
            style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
          >
            Dòng trụ sạc
          </span>
        ),
        selector: (row) => (
          <span className="cursor-pointer hover-opacity-60" onClick={() => { }}>
            {row?.chargeModel?.name}
          </span>
        ),
        minWidth: '240px',
        style: tableStyle.columns.link,
      },
      {
        id: 'TYPE',
        name: (
          <span
            className="letter-spacing-unset"
            style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
          >
            Loại trụ sạc
          </span>
        ),
        selector: (row) => row?.type,
        minWidth: '100px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'STATUS',
        name: (
          <span
            className="letter-spacing-unset"
            style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
          >
            Trạng thái
          </span>
        ),
        selector: (row) => {
          return (
            <KTLabel
              text={ChargeHelper.Utils.getChargePointStatusName(row?.status)}
              isInline={true}
              size={KTLabelSize.medium}
              additionalClassName={`label-light-${ChargeHelper.Utils.getChargePointStatusColor(
                row?.status
              )}`}
            />
          );
        },
        minWidth: '160px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'POWER',
        name: (
          <div className="d-flex flex-column align-items-end">
            <span
              className="letter-spacing-unset"
              style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
            >
              Công suất
            </span>
            <span
              className="letter-spacing-unset"
              style={UIHelper.buildFontStyle('500', '12px', AppColors.gray[700])}
            >
              kW
            </span>
          </div>
        ),
        selector: (row) => Utils.formatNumber(row?.power / 1000),
        minWidth: '100px',
        right: true,
        style: tableStyle.columns.normal,
      },
      {
        id: 'CONNECTOR_NUM',
        name: (
          <span
            className="letter-spacing-unset"
            style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
          >
            Số vòi sạc
          </span>
        ),
        selector: (row) => row?.chargeConnectors?.length,
        minWidth: '90px',
        right: true,
        style: tableStyle.columns.normal,
      },
      {
        id: 'PRICING_MODE',
        name: (
          <span
            className="letter-spacing-unset"
            style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
          >
            Phương thức tính giá
          </span>
        ),
        selector: (row) =>
          row?.pricing?.pricingMode
            ? _.find(
              ChargeHelper.Data.chargePointPricingModeOptions,
              (item) => item?.value === row?.pricing?.pricingMode
            )?.name
            : 'Mặc định',
        minWidth: '160px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'PRICE',
        name: (
          <div className="d-flex flex-column align-items-end">
            <span
              className="letter-spacing-unset"
              style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
            >
              Giá sạc
            </span>
            <span
              className="letter-spacing-unset"
              style={UIHelper.buildFontStyle('500', '12px', AppColors.gray[700])}
            >
              VNĐ/kWh
            </span>
          </div>
        ),
        selector: (row) => {
          if (row?.pricing?.pricingMode === ChargeConstants.ChargePricingMode.FLAT) {
            return (
              <span>
                {row?.pricing?.flatPrice !== null
                  ? Utils.formatNumber(row?.pricing?.flatPrice)
                  : 'Mặc định'}
              </span>
            );
          } else if (row?.pricing?.pricingMode === ChargeConstants.ChargePricingMode.HOURS) {
            return (
              <span>
                BT: {Utils.formatNumber(row?.pricing?.normalPrice)}, CĐ{' '}
                {Utils.formatNumber(row?.pricing?.peakPrice)}, TĐ:{' '}
                {Utils.formatNumber(row?.pricing?.offPeakPrice)}
              </span>
            );
          }
          return <span>{'Mặc định'}</span>;
        },
        right: true,
        minWidth: '240px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'FREE_DURATION',
        name: (
          <div className="d-flex flex-column align-items-end">
            <span
              className="letter-spacing-unset"
              style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
            >
              Thời gian chờ miễn phí
            </span>
            <span
              className="letter-spacing-unset"
              style={UIHelper.buildFontStyle('500', '12px', AppColors.gray[700])}
            >
              Phút
            </span>
          </div>
        ),
        selector: (row) =>
          row?.pricing !== null
            ? Utils.formatNumber(row?.pricing?.freeOverchargeDuration / 60)
            : 'Mặc định',
        right: true,
        minWidth: '160px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'OVER_PRICE',
        name: (
          <div className="d-flex flex-column align-items-end">
            <span
              className="letter-spacing-unset"
              style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
            >
              Phí sạc quá giờ
            </span>
            <span
              className="letter-spacing-unset"
              style={UIHelper.buildFontStyle('500', '12px', AppColors.gray[700])}
            >
              VNĐ/phút
            </span>
          </div>
        ),
        selector: (row) =>
          row?.pricing !== null ? Utils.formatNumber(row?.pricing?.overchargePrice) : 'Mặc định',
        right: true,
        minWidth: '140px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'RESERVATION_PRICE',
        name: (
          <div className="d-flex flex-column align-items-end">
            <span
              className="letter-spacing-unset"
              style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
            >
              Giá đặt giữ chỗ
            </span>
            <span
              className="letter-spacing-unset"
              style={UIHelper.buildFontStyle('500', '12px', AppColors.gray[700])}
            >
              VNĐ
            </span>
          </div>
        ),
        selector: (row) => Utils.formatNumber(row?.pricing?.reservationFee ?? 0),
        right: true,
        minWidth: '140px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'MAINTENANCE_SCHEDULE',
        name: (
          <div className="d-flex flex-column align-items-end">
            <span
              className="letter-spacing-unset"
              style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
            >
              Lịch bảo trì định kỳ
            </span>
          </div>
        ),
        selector: (row) => row?.maintenanceSchedule
          ? (
            <KTTooltip
              text={`<div>
              <span>Ngày bắt đầu: ${Utils.formatDateTimeToString(row?.maintenanceSchedule?.startDate, 'DD/MM/YYYY')}</span><br />
              <span>Chu kỳ: ${Utils.formatNumber(row?.maintenanceSchedule?.cycle)} ngày</span><br />
              <span>${row?.maintenanceSchedule?.description}</span>
              </div>`}
              textHeader={`Lịch bảo trì định kỳ: ${row?.pointCode}`}
              isHtmlContent
            >
              <span style={{
                textDecoration: 'underline'
              }}>
                Chu kỳ {Utils.formatNumber(row?.maintenanceSchedule?.cycle)} ngày
              </span>
            </KTTooltip >
          )
          : '',
        right: true,
        minWidth: '140px',
        style: tableStyle.columns.normal,
      },
      {
        name: (
          <span
            className="letter-spacing-unset"
            style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
          >
            Thao tác
          </span>
        ),
        width: isAdminAgency ? '100px' : '140px',
        style: {
          position: 'sticky',
          right: 0,
          backgroundColor: '#fff',
          borderLeft: '1px solid #ebedfe',
        },
        center: isAdminAgency,
        cell: (row) => {
          const actions = buildMoreActions(row);
          return UIHelper.buildMoreActionsButton(actions, true);
        },
      },
    ];
  }, [user, stationId, filter]);
  const [fullColumnIds, setFullColumnIds] = useState(
    fullColumns.filter(column => !column.omit).map(column => column.id).filter(Boolean) || []
  );
  const {
    columnIds,
    columns,
    onChangeCheckboxValue,
    resetDefaultTableColumn,
    onSelectAll,
    onUnSelectAll,
    fullColumnsSorted,
    fullColumnsIdSorted,
    saveChanges,
  } = useConfigTableColumn(prefKey, fullColumnIds, defaultColumnIds, fullColumns);

  // MARK: ---- METHODS ----
  const handleRefresh = (newFilter) => {
    setFilter({ ...newFilter });
  };

  const buildMoreActions = (row) => {
    const actions = [];
    // xem chi tiet
    if (Utils.checkPermission(permissionList, AppConstants.permissionCode.chargePointView.code)) {
      actions.push({
        text: 'Xem chi tiết',
        iconElement: <i className="fa-regular fa-eye" style={{ fontSize: '1.1rem' }} />,
        click: () => {
          if (!!stationId) {
            router.navigate(`point/${row?.pointId}`);
          } else {
            router.navigate(`${row?.pointId}`);
          }
        },
      });
    }
    if (!isAdminAgency) {
      if (Utils.checkPermission(permissionList, AppConstants.permissionCode.chargePointUpdate.code)) {
        actions.push({
          text: 'Chỉnh sửa',
          iconElement: <i className="fa-regular fa-edit" style={{ fontSize: '1.1rem' }} />,
          click: () => {
            if (!!stationId) {
              router.navigate(`point/${row?.pointId}/edit`);
            } else {
              router.navigate(`${row?.pointId}/edit`);
            }
          },
        });
        // hoan thanh bao tri
        if (row.status === ChargeConstants.ChargePointStatus.MAINTAINING) {
          actions.push({
            text: 'Hoàn thành bảo trì',
            iconElement: (
              <i className="fa-regular fa-clipboard-check" style={{ fontSize: '1.1rem' }} />
            ),
            click: () => {
              ChargeHelper.UI.showDialogConfirmCompleteMaintaining(row, () => {
                handleRefresh(filter);
              });
            },
          });
        }
        // thiet lap lich bao tri dinh ky
        actions.push({
          text: 'Thiết lập lịch bảo trì định kỳ',
          iconElement: (
            <i className="fa-regular fa-screwdriver-wrench" style={{ fontSize: '1.1rem' }} />
          ),
          click: () => {
            MaintainerHelper.UI.showDialogCreateMaintenanceSchedule(row, () => {
              handleRefresh(filter);
            });
          },
        });
        // di chuyen tru sac
        actions.push({
          text: 'Di chuyển trụ sạc',
          iconElement: (
            <i className="fa-regular fa-arrow-right-arrow-left" style={{ fontSize: '1.1rem' }} />
          ),
          click: () => {
            ChargeHelper.UI.showDialogConfirmMoveChargePoint(row, () => {
              handleRefresh(filter);
            });
          },
        });
        actions.push({
          text: 'Khởi động lại trụ sạc',
          iconElement: <i className="fa-regular fa-rotate-right" style={{ fontSize: '1.1rem' }} />,
          click: () => {
            ChargeHelper.UI.showDialogConfirmRestartChargePoint(row, () => {
              // khoi dong lai tru sac thanh cong
              handleRefresh(filter);
            });
          },
        });
        actions.push({
          text: 'Giới hạn công suất',
          iconElement: (
            <i className="fa-regular fa-arrows-from-dotted-line" style={{ fontSize: '1.1rem' }} />
          ),
          click: () => {
            ChargeHelper.UI.showDialogConfirmSetPowerLimitChargePoint(row, () => {
              // gioi han cong suat thanh cong
              handleRefresh(filter);
            });
          },
        });
        if (row.status !== ChargeConstants.ChargePointStatus.FAULT) {
          actions.push({
            text: 'Báo lỗi trụ sạc',
            iconElement: (
              <i className="fa-regular fa-triangle-exclamation" style={{ fontSize: '1.1rem' }} />
            ),
            click: () => {
              ChargeHelper.UI.showDialogConfirmReportErrorChargePoint(row, () => {
                // bao loi tru sac thanh cong
                handleRefresh(filter);
              });
            },
          });
        }
        if (
          [
            ChargeConstants.ChargePointStatus.FAULT,
            ChargeConstants.ChargePointStatus.MAINTAINING,
          ].includes(row.status)
        ) {
          actions.push({
            text: 'Khôi phục trụ sạc',
            iconElement: <i className="fa-regular fa-play" style={{ fontSize: '1.1rem' }} />,
            click: () => {
              ChargeHelper.UI.showDialogConfirmRestoreChargePoint(row, () => {
                // khoi phuc tru sac thanh cong
                handleRefresh(filter);
              });
            },
          });
        }
      }
      if (Utils.checkPermission(permissionList, AppConstants.permissionCode.chargePointDelete.code)) {
        actions.push({
          text: 'Xoá',
          iconElement: <i className="fa-regular fa-trash-can" style={{ fontSize: '1.1rem' }} />,
          click: () => {
            ChargeHelper.UI.showDialogConfirmDeleteChargePoint(row, () => {
              // xoa tru sac thanh cong
              handleRefresh(filter);
            });
          },
        });
      }
    }
    return actions;
  };

  const updateFilter = (key, value) => {
    if (!!stationId) {
      const updatedFilter = { ...filter, [key]: value }
      setFilter(updatedFilter);
      setFilterDropdown(updatedFilter);
    } else {
      Global.g_filterChargePointList = { ...Global.g_filterChargePointList, [key]: value };
      setFilter(Global.g_filterChargePointList);
      setFilterDropdown(Global.g_filterChargePointList);
    }
  };

  const [selectedChargeStation, setSelectedChargeStation] = useState(null);

  const dropdownChargeStationEl = useMemo(() => {
    return (
      <CustomDropdown2
        id="stationId"
        name="stationId"
        toggleElement={
          <button
            className={`btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between`}
            type="button"
          >
            {selectedChargeStation?.name ?? 'Tất cả'}
          </button>
        }
        selectedValue={selectedChargeStation}
        subTitleType="address"
        dropdownMenuItems={[{ name: 'Trạm sạc', role: 'STATION' }]}
        getSubItems={async (item) => {
          return ChargeHelper.API.requestGetChargeStationList({
            page: item?.page || 0,
            limit: 10,
            q: item?.q || '',
          }).then((data) => {
            return {
              values: data.chargeStations?.map((station) => ({
                id: station?.stationId,
                name: station?.name,
                item: station,
                address: ChargeHelper.Utils.getChargeStationFullAddress(station),
              })),
              total: data.total,
            };
          });
        }}
        dropdownMenuStyle={{ maxHeight: '30vh' }}
        dropdownMenuClassName="overflow-auto w-100"
        onChange={(value) => {
          setSelectedChargeStation(value);
          updateFilter('stationId', value?.id);
        }}
        isDropdownForm={true}
        titleSearchable={true}
        searchable={true}
        searchFormClassName="mx-3 my-1 search-form-border"
        ignoreConfirmClick={true}
        defaultValue={{
          name: 'Trạm sạc',
          role: 'STATION',
        }}
        footerEl={
          <div
            className="p-4 position-sticky sticky-bottom bg-white"
            style={{
              borderTop: `1px solid ${AppColors.gray[300]}`,
            }}
          >
            {UIHelper.buildNegativeButton('Bỏ chọn', () => {
              updateFilter('stationId', '');
              setSelectedChargeStation(null);
            })}
          </div>
        }
      />
    )
  }, [selectedChargeStation])

  return (
    <div className="px-4">
      {UIHelper.buildCard(
        <div className="pb-4">
          {/* header */}
          <div className="px-4 pt-4 pb-3 d-flex align-items-center justify-content-between">
            <div className="d-flex flex-column">
              <span style={UIHelper.buildFontStyle('600', '18px', AppColors.gray[800])}>
                Danh sách trụ sạc
              </span>
              <span style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[800])}>
                Tổng:{' '}
                <span style={UIHelper.buildFontStyle('600', '14px', AppColors.primary)}>
                  {Utils.formatNumber(total)} trụ sạc
                </span>
                <span style={{ color: AppColors.gray[600] }}> • </span>
                Số trụ sạc còn trống:{' '}
                <span style={UIHelper.buildFontStyle('600', '14px', AppColors.primary)}>
                  {Utils.formatNumber(availableTotal)}/{Utils.formatNumber(total)}
                </span>
              </span>
            </div>
            <div>
              {isAdmin && !!stationId && enabledEdit && Utils.checkPermission(permissionList, AppConstants.permissionCode.chargePointCreate.code) &&
                UIHelper.buildGreenButton(
                  'Thêm trụ sạc',
                  () => {
                    router.navigate('point/add');
                  },
                  <i className="fa-regular fa-circle-plus" />
                )}
            </div>
          </div>

          {/* table */}
          <div className="">
            {/* filter */}
            <div className="w-100 d-flex align-items-end flex-wrap pb-4 px-4">
              {/* tim kiem */}
              <div className="col-lg-3 py-2 pr-0 pr-xl-4 pr-lg-4 pl-0">
                {UIHelper.buildFormLabel('Tìm kiếm')}
                <KTFormSearch
                  name="search"
                  placeholder="Theo mã trụ sạc, mã OCPP, số điện thoại..."
                  value={filter?.q}
                  onSubmit={(text) => {
                    setFilter({ ...filter, q: text });
                  }}
                />
              </div>
              {/* loc theo tram sac */}
              {!!chargePointListFilter && (
                <div className={`flex-grow-1 py-2 pr-4 pt-1`}>
                  {UIHelper.buildFormLabel('Trạm sạc')}
                  {dropdownChargeStationEl}
                </div>
              )}

              {/* loc theo trang thai */}
              <KTDropdownFilter
                label='Trạng thái'
                filterKey="status"
                options={[
                  { name: 'Tất cả', value: '' },
                  ...ChargeHelper.Data.chargePointStatusOptions,
                ]}
                selectedValue={filter?.status}
                additionalClassName={`${'pr-0 pr-0 pr-xl-4'}`}
                handleChange={(value) => {
                  updateFilter('status', value);
                }}
              />
              {/* loc theo loai tru sac */}
              <KTDropdownFilter
                label='Loại trụ sạc'
                filterKey="type"
                options={[
                  { name: 'Tất cả', value: '' },
                  ...ChargeHelper.Data.chargePointTypeOptions,
                ]}
                selectedValue={filter?.type}
                additionalClassName={`${'pl-0 pr-0 pr-xl-4'}`}
                handleChange={(value) => {
                  updateFilter('type', value);
                }}
              />


              {/* bo loc */}
              {!!chargePointListFilter && (
                <div className="py-2 pr-4">
                  <DropdownFilterTable
                    filterComponent={[
                      <div>
                        <ChargePointListFilterView
                          isInDropdown={true}
                          chargePointListFilter={chargePointListFilter}
                          selectedChargeStation={selectedChargeStation}
                          setSelectedChargeStation={setSelectedChargeStation}
                          updateFilter={updateFilter}
                          filterDropdown={filterDropdown}
                          setFilterDropdown={setFilterDropdown}
                        />
                      </div>
                    ]}
                    resetDefaultTable={() => {
                      const updateFilterDropdown = {
                        ...filterDropdown,
                        page: 0,
                        limit: AppConfigs.defaultPageLimit,
                        q: '',
                        status: '',
                        type: '',
                        stationId: '',
                        vehicleType: '',
                        chargingSpeed: '',
                        isTestChargePoint: 0,
                      };
                      setFilterDropdown(updateFilterDropdown);
                      setFilter(updateFilterDropdown);
                      setSelectedChargeStation('');
                    }}
                    saveFilter={() => {
                      setFilter(filterDropdown);
                      Global.g_filterChargePointList = filterDropdown;
                    }}
                    isDefaultFilter={Utils.checkDefaultFilter(
                      ['page', 'limit', 'landlordId', 'isTestChargePoint'],
                      filter,
                      dateFilter.tatCaThoiGian,
                      false
                    )}
                  />
                </div>
              )}
              {/* cau hinh cot */}
              <div className="pr-0 py-2">
                <DropdownTableConfigColumn
                  columnIds={columnIds}
                  fullColumns={fullColumns}
                  fullColumnIds={fullColumnsIdSorted}
                  onChangeCheckboxValue={onChangeCheckboxValue}
                  saveChanges={saveChanges}
                  resetDefaultTableColumn={() => {
                    setFullColumnIds(defaultColumnIds);
                    resetDefaultTableColumn();
                  }}
                  onChangeFullColumnIds={(ids) => {
                    setFullColumnIds(ids);
                    const key = prefKey + '_FULL_COLUMN_IDS';
                    localStorage.setItem(key, ids.join('|'));
                  }}
                  onSelectAll={onSelectAll}
                  onUnSelectAll={onUnSelectAll}
                />
              </div>
            </div>

            <div className="px-4">
              <KTTable
                columns={columns}
                items={filteredItems}
                selectableRows={false}
                striped={false}
                compactGrid={false}
                onClickedRow={(row) => { }}
                onSelectedRowsChanged={(rows) => { }}
                onDoubleClickedRow={(row) => { }}
                noDataComponent={UIHelper.buildEmptyNoData(
                  'Không có trụ sạc nào trong trạm sạc này.'
                )}
              />
            </div>
          </div>


          {/* pagination */}
          <div className="px-4 mt-2">
            <KTPagination
              currentPage={filter?.page + 1}
              rowCount={total}
              rowsPerPage={filter?.limit}
              currentItemInPageCount={filteredItems?.length}
              onChangePage={(newPage) => {
                const iNewPage = parseInt(newPage);
                setFilter({
                  ...filter,
                  page: iNewPage - 1,
                });
              }}
              onChangeRowsPerPage={(newRowsPerPage) => {
                const iNewRowsPerPage = parseInt(newRowsPerPage);
                setFilter({
                  ...filter,
                  limit: iNewRowsPerPage,
                  page: 0,
                });
              }}
              loading={false}
              color={KTPaginationColor.success}
              appendText="trụ sạc"
            />
          </div>
        </div>,
        false,
        false,
        true,
        false,
        AppColors.gray[300],
        hasAppTab
      )}
    </div>
  );
}

export default ChargePointListView;
